import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@agms/ui'
import { ChevronDown, ChevronUp, Download, FileText } from 'lucide-react'
import { apiErrorMessage } from '../../hooks/useErrorHandler'
import { merchantReportingApi, type MerchantStatementRow } from '../../lib/api'

interface MerchantStatementsSectionProps {
  merchant: {
    id: string
    fiserv_mid: string | null
    fiserv_north_number?: string | null
  }
}

function statementDate(value: string): string {
  if (!value) return '—'
  const dateOnly = value.slice(0, 10)
  const parsed = new Date(`${dateOnly}T00:00:00`)
  return Number.isNaN(parsed.getTime()) ? dateOnly : parsed.toLocaleDateString()
}

function downloadName(row: MerchantStatementRow): string {
  return `statement-${row.merchant_number}-${row.statement_date.slice(0, 10)}.pdf`
}

export function MerchantStatementsSection({ merchant }: MerchantStatementsSectionProps) {
  const [expanded, setExpanded] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const hasFiservIdentifier = Boolean(merchant.fiserv_north_number?.trim() || merchant.fiserv_mid?.trim())

  const statementsQuery = useQuery({
    queryKey: ['merchant-statements', merchant.id],
    queryFn: () => merchantReportingApi.statements(merchant.id),
    enabled: expanded && hasFiservIdentifier,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const downloadMutation = useMutation({
    mutationFn: (row: MerchantStatementRow) => merchantReportingApi.statementDownload(merchant.id, row.statement_id),
    onSuccess: (blob, row) => {
      setDownloadError(null)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = downloadName(row)
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
    },
    onError: (error) => {
      setDownloadError(apiErrorMessage(error, 'Unable to download this statement. Refresh and try again.'))
    },
  })

  if (!hasFiservIdentifier) {
    return null
  }

  const rows = statementsQuery.data?.data ?? []

  return (
    <Card>
      <CardHeader className={expanded ? undefined : 'border-b-0'}>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle>Statements</CardTitle>
            <p className="mt-1 text-sm text-gray-500">Monthly merchant statements from Fiserv Reporting.</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 w-9 shrink-0 p-0"
            aria-label={expanded ? 'Collapse statements' : 'Expand statements'}
            aria-expanded={expanded}
            aria-controls={`merchant-statements-${merchant.id}`}
            title={expanded ? 'Collapse statements' : 'Expand statements'}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? <ChevronUp aria-hidden="true" className="h-5 w-5" /> : <ChevronDown aria-hidden="true" className="h-5 w-5" />}
          </Button>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent id={`merchant-statements-${merchant.id}`}>
          <div className="mb-4 flex gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
            <FileText aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            <p>Fiserv Reporting returns approximately the most recent three months of statements.</p>
          </div>

          {statementsQuery.isLoading && (
            <p className="py-5 text-center text-sm text-gray-500" role="status">Loading statements...</p>
          )}

          {statementsQuery.error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
              {apiErrorMessage(statementsQuery.error, 'Statements are unavailable. Refresh and try again.')}
            </div>
          )}

          {downloadError && (
            <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
              {downloadError}
            </div>
          )}

          {statementsQuery.isSuccess && rows.length === 0 && (
            <p className="py-5 text-center text-sm text-gray-500">
              No statements available. Fiserv Reporting returns approximately the most recent three months of statements.
            </p>
          )}

          {statementsQuery.isSuccess && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Statement Date</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Download</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row) => {
                    const downloading = downloadMutation.isPending
                      && downloadMutation.variables?.statement_id === row.statement_id

                    return (
                      <tr key={row.statement_id}>
                        <td className="px-3 py-3 text-gray-900">{statementDate(row.statement_date)}</td>
                        <td className="px-3 py-3 text-right">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            isLoading={downloading}
                            disabled={downloadMutation.isPending}
                            onClick={() => downloadMutation.mutate(row)}
                          >
                            {!downloading && <Download aria-hidden="true" className="mr-2 h-4 w-4" />}
                            Download PDF
                          </Button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}
